import { AuthUser } from "@/types";
import type { AppState } from "@/store";
import type { Actions } from "@/actions";
import { decodeJwt } from "@/core";
import { loadConfig } from "@/config";
import { Router } from "@/_router";
import toast from "react-hot-toast";
import { useUISlice } from "@/models/useUISlice";
import { getPlatform, getPlatformRuntime, isNativeApp } from "@/platform";
import { i18n } from "@/locales";
import { logBrowserCrash, logClientEvent } from "@/telemetry";
import {
  hasAccountIdentity,
  shouldAttachNativeAccount,
  shouldProtectGuestPurchase,
} from "@/auth-identity";
import { createActionError } from "@/client-error";
import { clearWebDictationHistory } from "@/web-dictation-interactions";

type AuthStoreState = Pick<AppState, "deviceId" | "userToken"> & {
  setDeviceId: (deviceId: string) => void;
};

type AuthStore = {
  getState: () => AuthStoreState;
};

let _authStore: AuthStore | undefined;
type AuthActions = {
  session: Actions["session"];
  user: Pick<
    Actions["user"],
    | "requestWebSignIn"
    | "confirmWebSignIn"
    | "webSignInWithGoogle"
    | "requestNativeSignIn"
    | "confirmNativeSignIn"
  >;
};

let _authActions: AuthActions | undefined;
let _guestSignInAttempt: Promise<string | undefined> | undefined;
let _signInAttemptId = 0;
const _authConfig = loadConfig();

export const configureAuthStore = (store: AuthStore): void => {
  _authStore = store;
};

export const configureAuthActions = (
  actions: AuthActions
): void => {
  _authActions = actions;
};

const _store = (): AuthStore => {
  if (!_authStore) {
    throw new Error("Auth store used before controller construction");
  }
  return _authStore;
};

const _actions = (): AuthActions => {
  if (!_authActions) {
    throw new Error("Auth actions used before controller construction");
  }
  return _authActions;
};

type SignInPrompt = NonNullable<
  ReturnType<typeof useUISlice.getState>["alertPromptSignIn"]
>;

const _patchSignInPrompt = (
  attemptId: number,
  patch: Partial<SignInPrompt>,
): boolean => {
  if (attemptId !== _signInAttemptId) return false;
  const current = useUISlice.getState().alertPromptSignIn;
  if (!current) return false;
  useUISlice.getState().setAlertPromptSignIn({ ...current, ...patch });
  return true;
};

const _freshDeviceId = (): string => {
  const randomBytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  return `${getPlatform()}_${Array.from(randomBytes, (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("")}`;
};

const _rotateGuestIdentity = async (): Promise<void> => {
  clearWebDictationHistory(window.localStorage);
  await _actions().session.end(_freshDeviceId());
};

export const useAuth = () => {
  return {
    signIn: useAuth.signIn,
    signInWithGoogle: useAuth.signInWithGoogle,
    currentAuthUser: useAuth.currentAuthUser,
    isAuthenticated: useAuth.isAuthenticated,
    signOut: useAuth.signOut,
  };
};

useAuth.signInAsGuest = async ({
  callback,
  onError,
  preserveSignInAttempt = false,
}: {
  callback?: (authToken: string) => void;
  onError?: () => void;
  preserveSignInAttempt?: boolean;
}) => {
  if (useAuth.isAuthenticated()) {
    const token = _store().getState().userToken;
    if (!token) throw new Error("Authenticated guest session is missing its token");
    callback?.(token);
    return;
  }

  _guestSignInAttempt ??= (async () => {
    let deviceId = _store().getState().deviceId;

    if (!deviceId) {
      deviceId = `${getPlatform()}_${(await getPlatformRuntime().device.getId()).replace(/:/g, "").replace(/-/g, "")}`;
      _store().getState().setDeviceId(deviceId);
    }

    let result = await _actions().session.signInAsGuest({
      deviceId,
      deviceType: getPlatform(),
      deviceLanguage: navigator?.language?.toLowerCase() || "en",
      deviceTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    if ("error" in result && result.error === "DeviceIdentityLinked") {
      // A pre-fix client may have persisted an identity that the server has
      // already attached to an account. Rotate once and resume as a genuinely
      // new guest; never mint or reuse a guest token for the linked source.
      await _rotateGuestIdentity();
      deviceId = _store().getState().deviceId;
      if (!deviceId) return undefined;
      result = await _actions().session.signInAsGuest({
        deviceId,
        deviceType: getPlatform(),
        deviceLanguage: navigator?.language?.toLowerCase() || "en",
        deviceTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
    }
    if ("error" in result) return undefined;

    const authenticated = await useAuth.onAuthSuccess(result.value, undefined, {
      preserveSignInAttempt,
    });
    if (!authenticated) return undefined;
    logClientEvent("auth.completed", { method: "guest" });
    return result.value;
  })()
    .catch((error) => {
      logBrowserCrash("auth.guestAuthentication", error);
      return undefined;
    })
    .finally(() => {
      _guestSignInAttempt = undefined;
    });

  const token = await _guestSignInAttempt;
  if (!token) {
    onError?.();
    return;
  }
  callback?.(token);
};

useAuth.signInWithGoogleWeb = async (): Promise<{ accessToken: string }> => {
  return new Promise((resolve, reject) => {
    const client = (window as any)?.google?.accounts?.oauth2["initTokenClient"](
      {
        client_id: _authConfig.googleClientId,
        scope: `openid profile email`,
        callback: (response: { access_token: string }) => {
          resolve({ accessToken: response.access_token });
        },
        error_callback: (nonOAuthError: any) => {
          reject(nonOAuthError);
        },
      }
    );
    client.requestAccessToken();
  });
};

useAuth.signInWithGoogle = async ({
  callback,
}: {
  callback?: (authToken: string) => void;
}) => {
  // Google account sign-in is intentionally web-only. Native users retain
  // their device-backed guest session and can use email if they explicitly
  // choose to attach an account.
  if (isNativeApp()) return;

  const currentUser = useAuth.currentAuthUser();
  if (useAuth.isAuthenticated() && hasAccountIdentity(currentUser)) {
    const token = _store().getState().userToken;
    if (!token) throw new Error("Authenticated Google session is missing its token");
    return callback?.(token);
  }

  try {
    const googleResponse = await useAuth.signInWithGoogleWeb();
    const googleAccessToken = googleResponse.accessToken;

    const attachWebAccount =
      useAuth.isAuthenticated() && !hasAccountIdentity(currentUser);
    let result = attachWebAccount
      ? await _actions().user.webSignInWithGoogle(googleAccessToken)
      : await _actions().session.signInWithGoogle({
          googleAccessToken,
          deviceTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          deviceLanguage: navigator.language.toLowerCase(),
          deviceType: getPlatform(),
        });

    if (
      attachWebAccount &&
      "error" in result &&
      result.error === "DeviceIdentityLinked" &&
      await useAuth.recoverLinkedGuest()
    ) {
      result = await _actions().user.webSignInWithGoogle(googleAccessToken);
    }

    if ("error" in result) {
      return toast.error(i18n.t("useAuth.googleSignInFailed"));
    }

    if (!(await useAuth.onAuthSuccess(result.value, callback))) {
      return toast.error(i18n.t("useAuth.googleSignInFailed"));
    }
    logClientEvent("auth.completed", {
      method: attachWebAccount ? "googleWebDevice" : "google",
    });
  } catch (e) {
    const errorCode = (e as { error?: string } | undefined)?.error;

    if (errorCode !== "popup_closed_by_user") {
      logBrowserCrash("auth.googleSignIn", e);
      console.error("Google sign-in failed:", e);
      toast.error(i18n.t("useAuth.googleSignInFailed"));
    }
  }
};

useAuth.isAuthenticated = (): boolean => {
  return Boolean(useAuth.currentAuthUser());
};

useAuth.isValidEmail = (email: string) => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email.trim());
};

useAuth.onAuthSuccess = async (
  authToken: string,
  callback?: (authToken: string) => void,
  options: { preserveSignInAttempt?: boolean } = {},
): Promise<boolean> => {
  try {
    const user = useAuth.currentAuthUser(authToken);
    if (!user) throw createActionError("InvalidAccessToken");
    const started = await _actions().session.start(authToken);
    if ("error" in started) throw createActionError(started.error);
    if (!options.preserveSignInAttempt) {
      _signInAttemptId += 1;
      useUISlice.getState().setAlertPromptSignIn(undefined);
    }
  } catch (e) {
    logBrowserCrash("auth.userAuthentication", e);
    return false;
  }
  callback?.(authToken);
  return true;
};

useAuth.recoverLinkedGuest = async (): Promise<boolean> => {
  try {
    await _rotateGuestIdentity();
    let recovered = false;
    await useAuth.signInAsGuest({
      preserveSignInAttempt: true,
      callback: () => {
        recovered = true;
      },
    });
    return recovered;
  } catch (error) {
    logBrowserCrash("auth.linkedGuestRecovery", error);
    return false;
  }
};

useAuth.prepareAccountSwitch = async (): Promise<void> => {
  const currentUser = useAuth.currentAuthUser();
  if (
    useAuth.isAuthenticated() &&
    hasAccountIdentity(currentUser)
  ) {
    await _rotateGuestIdentity();
  }
};

useAuth.currentAuthUser = (givenToken?: string): AuthUser | undefined => {
  const userToken = givenToken || _store().getState().userToken;

  if (!userToken) return undefined;

  const authUser = decodeJwt(userToken);
  if (
    !authUser ||
    typeof authUser.userId !== "string" ||
    !authUser.userId.trim() ||
    typeof authUser.email !== "string" ||
    !authUser.email.trim()
  ) return undefined;

  if (
    typeof authUser.exp !== "number" ||
    !Number.isFinite(authUser.exp) ||
    authUser.exp <= Date.now() / 1000
  ) {
    return undefined;
  }

  return authUser;
};

useAuth.signIn = ({
  forceSignIn = false,
  callback,
  onCancel,
  onError,
  modalProps,
}: {
  forceSignIn?: boolean;
  callback?: (authToken: string) => void;
  onCancel?: () => void;
  onError?: () => void;
  modalProps?: {
    title?: string;
    subtitle?: string;
    defaultValue?: string;
    buttonText?: string;
    hideGoogle?: boolean;
    googleButtonText?: string;
  };
}) => {
  // Every browser account starts from the durable device identity. This keeps
  // checkout, transcripts, and sign-in on one user even when /auth is the
  // first page opened (including the Chrome hosted-auth handoff).
  if (!isNativeApp() && !useAuth.isAuthenticated()) {
    void useAuth.signInAsGuest({
      callback: () => {
        useAuth.signIn({ forceSignIn, callback, onCancel, onError, modalProps });
      },
      onError: () => { toast.error(i18n.t("useAuth.signInFailed")); onError?.(); },
    });
    return;
  }

  const currentUser = useAuth.currentAuthUser();
  if (useAuth.isAuthenticated() && hasAccountIdentity(currentUser)) {
    const token = _store().getState().userToken;
    if (!token) throw new Error("Authenticated account session is missing its token");
    return callback?.(token);
  }
  const attachNativeAccount = shouldAttachNativeAccount({
    isNative: isNativeApp(),
    isAuthenticated: useAuth.isAuthenticated(),
    user: currentUser,
  });
  const attachWebAccount =
    !isNativeApp() &&
    useAuth.isAuthenticated() &&
    !hasAccountIdentity(currentUser);
  const emailVerificationMode = attachNativeAccount
    ? "native"
    : attachWebAccount
      ? "web"
      : undefined;

  const attemptId = ++_signInAttemptId;

  useUISlice.getState().setAlertPromptSignIn({
    title: modalProps?.title || i18n.t("useAuth.title"),
    subtitle: modalProps?.subtitle || i18n.t("useAuth.subtitle"),
    placeholder: i18n.t("useAuth.signInEmailPlaceholder"),
    defaultValue: modalProps?.defaultValue,
    googleButtonText: modalProps?.googleButtonText,
    hideDismissIcon: forceSignIn && !onCancel,
    hideCancel: forceSignIn,
    onDismiss: () => {
      if (_signInAttemptId === attemptId) { _signInAttemptId += 1; onCancel?.(); }
    },
    onGoogleSignIn: isNativeApp() || modalProps?.hideGoogle
      ? undefined
      : () => {
          if (!_patchSignInPrompt(attemptId, { loading: true })) return;
          void useAuth
            .signInWithGoogle({
              callback,
            })
            .finally(() => {
              _patchSignInPrompt(attemptId, { loading: false });
            });
        },
    shouldPersistBackdropClick: true,
    button: {
      text: modalProps?.buttonText || i18n.t("useAuth.signInButton"),
      onPress: async (input) => {
        const email = input.trim().toLowerCase();
        const validEmail = useAuth.isValidEmail(email);

        if (!validEmail) {
          console.warn("useAuth: Invalid email");
          return toast.error(i18n.t("useAuth.invalidEmail"));
        }

        if (!_patchSignInPrompt(attemptId, {
          onGoogleSignIn: undefined,
          defaultValue: email,
          loading: true,
        })) return;

        const requestOneTimeCode = () => emailVerificationMode === "native"
          ? _actions().user.requestNativeSignIn(email)
          : emailVerificationMode === "web"
            ? _actions().user.requestWebSignIn(email)
            : _actions().session.requestOneTimeCode({
              email,
              deviceType: getPlatform(),
              deviceLanguage: navigator.language,
              deviceTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            });
        let otpRequestResult = await requestOneTimeCode();
        if (
          emailVerificationMode &&
          "error" in otpRequestResult &&
          otpRequestResult.error === "DeviceIdentityLinked" &&
          await useAuth.recoverLinkedGuest()
        ) {
          otpRequestResult = await requestOneTimeCode();
        }

        if (!_patchSignInPrompt(attemptId, {
          onGoogleSignIn: undefined,
          loading: false,
        })) return;

        if ("error" in otpRequestResult) {
          console.warn(
            "useAuth: One-time code request failed",
            otpRequestResult.error,
          );
          return toast.error(
            i18n.t(
              otpRequestResult.error === "InvalidEmail"
                ? "useAuth.invalidEmail"
                : "useAuth.signInFailed",
            ),
          );
        }

        useUISlice.getState().setAlertPromptSignIn({
          title: i18n.t("useAuth.oneTimeCodeTitle"),
          subtitle: i18n.t("useAuth.oneTimeCodeSubtitle", { email }),
          placeholder: i18n.t("useAuth.oneTimeCodePlaceholder"),
          inputMode: "numeric",
          autoComplete: "one-time-code",
          pattern: "[0-9]*",
          hideCancel: true,
          hideDismissIcon: forceSignIn && !onCancel,
          onDismiss: () => {
            if (_signInAttemptId === attemptId) { _signInAttemptId += 1; onCancel?.(); }
          },
          shouldPersistBackdropClick: true,
          button: {
            text: i18n.t("useAuth.oneTimeCodeButton"),
            onPress: async (input) => {
              if (!_patchSignInPrompt(attemptId, {
                defaultValue: input,
                loading: true,
              })) return;

              const oneTimeCode = Number.parseInt(input, 10);
              const otpVerifyResult = emailVerificationMode === "native"
                ? await _actions().user.confirmNativeSignIn(oneTimeCode)
                : emailVerificationMode === "web"
                  ? await _actions().user.confirmWebSignIn(oneTimeCode)
                  : await _actions().session.confirmOneTimeCode({
                    email,
                    oneTimeCode,
                  });

              if (!_patchSignInPrompt(attemptId, {
                loading: false,
              })) return;

              if (
                "error" in otpVerifyResult &&
                (otpVerifyResult.error === "InvalidOneTimeCode" ||
                  otpVerifyResult.error === "OneTimeCodeExpired")
              ) {
                return toast.error(i18n.t("useAuth.invalidCode"));
              }

              if (
                "error" in otpVerifyResult &&
                otpVerifyResult.error === "DeviceIdentityLinked"
              ) {
                if (await useAuth.recoverLinkedGuest()) {
                  useAuth.signIn({
                    forceSignIn,
                    callback,
                    onCancel,
                    onError,
                    modalProps: { ...modalProps, defaultValue: email },
                  });
                  return;
                }
                return toast.error(i18n.t("useAuth.signInFailed"));
              }

              if ("error" in otpVerifyResult) {
                return toast.error(i18n.t("useAuth.signInFailed"));
              }

              if (await useAuth.onAuthSuccess(otpVerifyResult.value, callback)) {
                logClientEvent("auth.completed", {
                  method: "email",
                });
              } else {
                return toast.error(i18n.t("useAuth.signInFailed"));
              }
            },
          },
        });
      },
    },
  });
};

useAuth.protectGuestPurchase = ({
  email,
  callback,
}: {
  email?: string;
  callback?: (authToken: string) => void;
} = {}): boolean => {
  const currentUser = useAuth.currentAuthUser();
  if (
    !shouldProtectGuestPurchase({
      isAuthenticated: useAuth.isAuthenticated(),
      user: currentUser,
    })
  ) {
    return false;
  }

  const purchaseEmail = email?.trim();
  useAuth.signIn({
    modalProps: {
      title: i18n.t("useAuth.protectPurchaseTitle"),
      subtitle: purchaseEmail
        ? i18n.t("useAuth.protectPurchaseOptionsSubtitle", {
            email: purchaseEmail,
          })
        : i18n.t("useAuth.subtitle"),
      defaultValue: purchaseEmail,
      buttonText: i18n.t("useAuth.protectPurchaseButton"),
      googleButtonText: i18n.t("useAuth.protectPurchaseGoogleButton"),
    },
    callback,
  });
  return true;
};

useAuth.signOut = async (callback?: () => void, navigate: () => void = () => Router.push("/")) => {
  // A device identity is permanently linked after it is attached to
  // an account. Reusing it after sign-out would make the next account attach
  // target that old link and fail with DeviceIdentityLinked. Rotate it in the
  // same persisted state update that clears the session so reload cannot
  // restore the linked identity.
  await _rotateGuestIdentity();
  navigate();
  callback && callback();
};
